<script setup lang="ts">
import { isElementInViewport } from '@@/bits/dom'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { postColorRgb } from '@@/bits/post_color'
import { defineAsyncComponent } from '@@/bits/vue'
import { WallWishGroupByTypes, WallWishGroupByValue } from '@@/enums'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import { OzPlainButtonColorScheme, OzPlainButtonSizePreset } from '@@/library/v4/components/OzPlainButton.vue'
import { OzSkewedButtonSizePreset } from '@@/library/v4/components/OzSkewedButton.vue'
import OzTooltip from '@@/library/v4/components/OzTooltip.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceGroupActionStore } from '@@/pinia/surface_group_action_store'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfaceOnboardingPanelStore } from '@@/pinia/surface_onboarding_panel'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostGroupByStore } from '@@/pinia/surface_post_group_by_store'
import { MAX_SINGLE_SELECT_OPTION_TEXT_LENGTH } from '@@/pinia/surface_post_properties'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'
import { useSurfaceTimelineFormatStore } from '@@/pinia/surface_timeline_format_store'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { LabelledSection, PostAttributes, PostColor, WallGroupPost } from '@@/types'
import SurfaceSectionTitle from '@@/vuecomponents/SurfaceSectionTitle.vue'
import type { LongPressEvent } from '@@/vuedirectives/emit_long_press'
import { flip, offset, shift } from '@floating-ui/vue'
import { storeToRefs } from 'pinia'
import type { ComponentPublicInstance } from 'vue'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'

const OzSkewedButton = defineAsyncComponent(() => import('@@/library/v4/components/OzSkewedButton.vue'))
const OzPlainButton = defineAsyncComponent(() => import('@@/library/v4/components/OzPlainButton.vue'))
const OzIcon = defineAsyncComponent(() => import('@@/library/v4/components/OzIcon.vue'))

// #region - Tooltip
const xAddPostDisabledButtonTooltip = ref(false)

// Add reactive state for section-specific button tooltips
const sectionButtonRefs = ref<Record<string, any>>({})

const setSectionButtonRef = (buttonId: string, el: any) => {
  if (el) sectionButtonRefs.value[buttonId] = el
}
// #endregion

const props = withDefaults(
  defineProps<{
    section: WallGroupPost
    useDefaultBgColor?: boolean
    hideButtons?: boolean
    xStartSkewedEdge?: boolean
    xRoundedStart?: boolean
    xMoreButton?: boolean
  }>(),
  {
    useDefaultBgColor: undefined,
    hideButtons: undefined,
    xStartSkewedEdge: false,
    xRoundedStart: false,
    xMoreButton: true,
  },
)

const emit = defineEmits<{
  (name: 'long-press', event: LongPressEvent): void
  (name: 'resize', value: DOMRectReadOnly): void
  (name: 'before-unmount'): void
}>()

const surfacePostsStore = useSurfacePostsStore()
const surfaceMapStore = useSurfaceMapStore()
const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
const surfaceOnboardingPanelStore = useSurfaceOnboardingPanelStore()
const surfaceWishArrangementStore = useSurfaceWishArrangementStore()

const {
  backgroundColor,
  backgroundLuminance,
  colorScheme,
  dir,
  fontId,
  isGrid,
  isMap,
  isReadOnly,
  skinPrimaryVariantColorAsRGB,
  isFrozen,
  isMatrix,
  isShelf,
  isStream,
  isTimeline,
  isTimelineV1,
  isTable,
  isPostSizeStandard,
  isPostSizeWide,
} = storeToRefs(useSurfaceStore())
const { isDraggingPostInNonManuallySortedWall } = storeToRefs(useSurfacePostsDragAndDropStore())
const { canIModerate, canIPost } = storeToRefs(useSurfacePermissionsStore())
const surfacePostGroupByStore = useSurfacePostGroupByStore()
const {
  groupUnderActionId,
  groupUnderActionRightClickCoordinates,
  groupBeingRenamedId,
  canEditGroupNameInline,
  xGroupSpecificButtons,
} = storeToRefs(surfacePostGroupByStore)
const surfaceTimelineFormatStore = useSurfaceTimelineFormatStore()
const surfaceGroupActionStore = useSurfaceGroupActionStore()
const { isBreakout } = storeToRefs(useSurfaceShareLinksStore())

const canEditSectionName = computed(() => {
  return (
    canIModerate.value &&
    !isFrozen.value &&
    !surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop &&
    (!surfaceWishArrangementStore.isGroupByColor ||
      (surfaceWishArrangementStore.isGroupByColor && !canEditGroupNameInline.value)) &&
    !surfaceWishArrangementStore.isGroupByAuthor
  )
})

const shouldHideSectionButtonsWhenDragging = computed(
  () => !isShelf.value && isDraggingPostInNonManuallySortedWall.value,
)

const sectionTitleBackgroundColor = computed(() => {
  if (surfaceWishArrangementStore.isGroupByColor) {
    const rgbColor = postColorRgb({
      postColor: props.section.id as PostColor,
      isLightColorScheme: colorScheme.value === 'light',
    })
    return `rgb(${rgbColor.r}, ${rgbColor.g}, ${rgbColor.b})`
  }

  if (props.useDefaultBgColor === true) return undefined

  if (isTimelineV1.value) return surfaceTimelineFormatStore.adjustedPrimaryBackgroundColorForSectionTitle

  return skinPrimaryVariantColorAsRGB.value ?? backgroundColor.value
})

function calculateMenuPositionAvoidingOnboardingPanel(x: number): { isCovered: boolean; adjustedX: number } {
  const onboardingPanel = document.getElementById('surface-blank-onboarding-panel')
  if (!onboardingPanel) return { isCovered: false, adjustedX: x }

  const onboardingPanelRect = onboardingPanel.getBoundingClientRect()
  const menuWidth = 256 // Width of the action menu

  if (x + menuWidth > onboardingPanelRect.left) {
    const adjustedX = onboardingPanelRect.left - menuWidth - 8 // 8px buffer from the onboarding panel
    return { isCovered: true, adjustedX }
  }

  return { isCovered: false, adjustedX: x }
}

function handleOpenSectionActionMenu(sectionId: WallGroupPost['id'], e?: MouseEvent): void {
  if (surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop) return

  groupUnderActionId.value = sectionId

  const isOpeningAtCoordinates = e?.pageX != null && e?.pageY != null

  if (isOpeningAtCoordinates) {
    if (surfaceOnboardingPanelStore.isBlankOnboardingPanel) {
      const { adjustedX } = calculateMenuPositionAvoidingOnboardingPanel(e.pageX)
      groupUnderActionRightClickCoordinates.value = {
        x: adjustedX,
        y: e.pageY,
      }
    } else {
      groupUnderActionRightClickCoordinates.value = { x: e.pageX, y: e.pageY }
    }
  } else {
    groupUnderActionRightClickCoordinates.value = null
  }
}

function startEditingSectionTitle(sectionId: LabelledSection['id']): void {
  if (sectionId) surfacePostGroupByStore.startRenamingGroup(sectionId)
}

const $el = ref<HTMLElement | null>(null)
const addPostButtonRef = ref<InstanceType<typeof OzBaseButton> | null>(null)

function emitHeaderResize(): void {
  if ($el.value) {
    emit('resize', $el.value.getBoundingClientRect())
  }
}

watch(groupBeingRenamedId, (newGroupBeingRenamedId) => {
  if (newGroupBeingRenamedId == null) return
  nextTick(() => {
    const element = document.getElementById(`section-title-${newGroupBeingRenamedId}`)
    if (element && !isElementInViewport(element)) {
      element?.scrollIntoView(true)
    }
  })
})

onBeforeUnmount(() => {
  emit('before-unmount')
})

const sectionAddPostButton = computed(() => ({
  shouldShow:
    canIPost.value &&
    !isReadOnly.value &&
    !isBreakout.value &&
    !isFrozen.value &&
    !surfaceWishArrangementStore.isGroupByAuthor,
  disabled:
    surfacePostGroupByStore.currentSingleSelectProperty?.required === true &&
    props.section.id === WallWishGroupByValue.NoValue,
  disabledTooltipText: surfacePostGroupByStore.addPostButtonDisabledTooltipText,
  icon: 'plus_thin',
  label: __('Add post to this section'),
  onClick: () => {
    const payload: PostAttributes = {}
    switch (surfaceWishArrangementStore.wishGroupBy) {
      case WallWishGroupByTypes.Section:
        payload.wall_section_id = props.section.id as number
        break
      case WallWishGroupByTypes.Color:
        // We store default color as null in the backend
        payload.color = props.section.id === 'default' ? undefined : (props.section.id as PostColor)
        break
      default: {
        // We don't show add post button when group by author or none
        // This means we only go into this case when group by single select
        if (
          surfaceWishArrangementStore.customPropIdFromWishGroupBy != null &&
          props.section.id != null &&
          props.section.id !== WallWishGroupByValue.NoValue
        ) {
          payload.custom_properties = {
            [surfaceWishArrangementStore.customPropIdFromWishGroupBy]: props.section.id,
          }
        }
        break
      }
    }
    if (isMap.value) {
      const mapAttributes =
        payload.wall_section_id == null ? payload : { ...payload, sectionId: payload.wall_section_id }
      surfaceMapStore.startPickingLocation(mapAttributes)
    } else {
      surfacePostsStore.startNewPost({ attributes: payload })
    }
  },
  id: `section-add-post-button-${props.section.id}`,
  testId: `${'title' in props.section ? props.section.title : props.section.id}SectionAddPostButton`,
}))

const sectionActionsButton = computed(() => ({
  shouldShow:
    !isReadOnly.value &&
    !isBreakout.value &&
    !surfaceWishArrangementStore.isGroupByAuthor &&
    surfaceGroupActionStore.actionListGroups.length > 0 &&
    (!xGroupSpecificButtons.value || !surfaceGroupActionStore.actionListGroupsOnlyContainsAddPost),
  disabled: false,
  disabledTooltipText: undefined,
  icon: 'dot_3_horizontal',
  label: __('Open section actions menu'),
  onClick: () => handleOpenSectionActionMenu(props.section.id),
  id: `section-action-menu-button-${props.section.id}`,
  testId: `${'title' in props.section ? props.section.title : props.section.id}SectionTitleMoreButton`,
}))

const sectionSpecificButtons = computed(() =>
  [sectionAddPostButton.value, sectionActionsButton.value].filter((button) => button.shouldShow),
)

const xPlusButtonBelowGroupHeader = computed(() => {
  return (
    isShelf.value &&
    sectionAddPostButton.value.shouldShow &&
    !surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop &&
    !props.hideButtons
  )
})

const onClickSectionTitle = (sectionId: WallGroupPost['id'], e?: MouseEvent): void => {
  // Group by author doesn't have an action menu
  if (surfaceWishArrangementStore.isGroupByAuthor) return
  // If '+' button is visible and action list only contains 'Add post' button, we don't need to open the actions menu
  if (
    surfaceGroupActionStore.actionListGroupsOnlyContainsAddPost &&
    (xGroupSpecificButtons.value || xPlusButtonBelowGroupHeader.value)
  )
    return

  handleOpenSectionActionMenu(sectionId, e)
}

// There's a possibility that some elements inside $el is not rendered yet.
// Therefore we also need to watch for whenever child elements of $el are rendered/hidden.
watch(addPostButtonRef, () => {
  emitHeaderResize()
})

const sectionTitleRef = ref<ComponentPublicInstance | null>(null)

const sectionTitle = computed(() => {
  return 'title' in props.section ? props.section.title : undefined
})

const sectionTitleDarkMode = computed(() => {
  if (isAppUsing('groupPostsV2') && surfaceWishArrangementStore.isGroupByColor) {
    return colorScheme.value === 'dark'
  }

  return isTimelineV1.value ? backgroundLuminance.value === 'light' : backgroundLuminance.value === 'dark'
})

defineExpose({
  sectionTitleRef,
})
</script>

<template>
  <header
    ref="$el"
    :class="{ flex: true, 'flex-col': isShelf, 'flex-row items-end': !isShelf, 'justify-between': isMap }"
    @long-press="$emit('long-press', $event)"
  >
    <SurfaceSectionTitle
      :id="'ariaLabelledby' in section ? section.ariaLabelledby : undefined"
      ref="sectionTitleRef"
      :section-id="section.id"
      :class="[isMap && 'me-3 desktop:me-2.5', 'surface-group-title', isShelf && 'max-w-[calc(100%-2px)]']"
      :background-color="sectionTitleBackgroundColor"
      :can-edit="canEditSectionName"
      :can-edit-inline="canEditGroupNameInline"
      :dark-mode="sectionTitleDarkMode"
      :dark-mode-interactive-state="isTimelineV1 ? colorScheme === 'dark' : backgroundLuminance === 'dark'"
      :dir="dir"
      :edit-mode="section.id === groupBeingRenamedId"
      :x-more-button="
        xMoreButton &&
        !isReadOnly &&
        !surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop &&
        !surfaceWishArrangementStore.isGroupByAuthor &&
        surfaceGroupActionStore.actionListGroups.length > 0 &&
        !surfaceGroupActionStore.actionListGroupsOnlyContainsAddPost
      "
      :x-start-skewed-edge="xStartSkewedEdge"
      :x-rounded-start="xRoundedStart"
      :font-id="fontId"
      :title="sectionTitle"
      :is-grid="isGrid"
      :is-matrix="isMatrix"
      :is-shelf="isShelf"
      :is-stream="isStream"
      :is-map="isMap"
      :is-timeline="isTimeline"
      :is-timeline-v1="isTimelineV1"
      :is-table="isTable"
      :avatar-src="section.avatar"
      :section-title-max-length="
        surfaceWishArrangementStore.isGroupBySingleSelect ? MAX_SINGLE_SELECT_OPTION_TEXT_LENGTH : undefined
      "
      @open-actions="onClickSectionTitle(section.id)"
      @open-actions-at-coordinates="(e) => onClickSectionTitle(section.id, e)"
      @start-editing="startEditingSectionTitle(section.id)"
      @finish-editing="surfacePostGroupByStore.updateGroupTitle"
      @cancel-editing="surfacePostGroupByStore.stopRenamingGroup()"
      @resize="emitHeaderResize"
    />
    <div
      v-if="xGroupSpecificButtons && !hideButtons"
      :class="[
        'flex flex-row space-s-1 surface-section-action-buttons',
        shouldHideSectionButtonsWhenDragging && 'invisible',
      ]"
    >
      <template v-for="button in sectionSpecificButtons" :key="button.id">
        <component
          :is="isMap ? OzPlainButton : OzSkewedButton"
          :ref="(el) => setSectionButtonRef(button.id, el)"
          :id="button.id"
          :data-testid="button.testId"
          :class="[
            'shrink-0',
            isMap
              ? ['w-6 h-6 p-1']
              : [
                  'transition-shadow motion-reduce:transition-none',
                  !button.disabled && {
                    'hover-overlay-light': backgroundLuminance === 'light',
                    'hover-overlay-dark': backgroundLuminance === 'dark',
                  },
                  // Disabled styles - apply to background and content, but not focus ring
                  button.disabled && [
                    'opacity-50',
                    '[&:focus-visible]:!opacity-100', // Override opacity for focus state to preserve focus ring color
                  ],
                ],
          ]"
          :size-preset="isMap ? OzPlainButtonSizePreset.H24px : OzSkewedButtonSizePreset.H40px"
          :color-scheme="isMap && OzPlainButtonColorScheme.Secondary"
          :style="
            !isMap && {
              backgroundColor:
                isAppUsing('groupPostsV2') && surfaceWishArrangementStore.isGroupByColor
                  ? sectionTitleBackgroundColor
                  : skinPrimaryVariantColorAsRGB || backgroundColor,
            }
          "
          :title="button.label"
          :aria-label="button.label"
          :dark-mode="
            isMap || (isAppUsing('groupPostsV2') && surfaceWishArrangementStore.isGroupByColor)
              ? colorScheme === 'dark'
              : backgroundLuminance === 'dark'
          "
          :dir="dir"
          :disabled="button.disabled"
          @click="button.onClick"
          @focus="button.disabled && button.disabledTooltipText ? (xAddPostDisabledButtonTooltip = true) : null"
          @blur="xAddPostDisabledButtonTooltip = false"
          @mouseenter="button.disabled && button.disabledTooltipText ? (xAddPostDisabledButtonTooltip = true) : null"
          @mouseleave="xAddPostDisabledButtonTooltip = false"
          @touchstart="
            button.disabled && button.disabledTooltipText
              ? (xAddPostDisabledButtonTooltip = !xAddPostDisabledButtonTooltip)
              : null
          "
        >
          <OzIcon :name="button.icon" :size="16" />
        </component>
        <!-- Need to use Teleport to ensure the tooltip is rendered above posts in Table format -->
        <Teleport to="#surface-container">
          <OzTooltip
            v-if="button.disabled && xAddPostDisabledButtonTooltip"
            :anchor="sectionButtonRefs[button.id].$el"
            :config="{
              placement: 'top',
              middleware: [
                offset({
                  mainAxis: 8,
                }),
                flip(),
                shift(),
              ],
            }"
            class="max-w-xs z-10"
          >
            <p>{{ button.disabledTooltipText }}</p>
          </OzTooltip>
        </Teleport>
      </template>
    </div>
    <OzBaseButton
      v-if="xPlusButtonBelowGroupHeader"
      ref="addPostButtonRef"
      :disabled="sectionAddPostButton.disabled"
      :aria-label="sectionAddPostButton.label"
      :title="sectionAddPostButton.label"
      :class="[
        'flex',
        'items-center',
        'justify-center',

        'transition-all motion-reduce:transition-none',

        // Size & shape
        isShelf ? [isPostSizeStandard && 'mt-3.5', isPostSizeWide && 'mt-4'] : 'mt-3 tablet-portrait:mt-4',
        'rounded-lg',
        'w-full h-9',
        'p-0',

        // Base
        'backdrop-blur-sm',

        // Focus
        'focus-visible:outline-none',
        'focus-visible:ring-2',
        {
          'ring-grape-500': backgroundLuminance === 'light',
          'ring-canary-500': backgroundLuminance === 'dark',
        },

        !sectionAddPostButton.disabled && [
          // Base
          {
            'bg-[rgba(255,255,255,0.32)]': backgroundLuminance === 'light',
            'bg-[rgba(0,0,0,0.24)]': backgroundLuminance === 'dark',
            'text-dark-text-200': backgroundLuminance === 'light',
            'text-light-text-200': backgroundLuminance === 'dark',
          },

          // Hover
          {
            'hover-hover:hover:bg-light-ui-100': backgroundLuminance === 'light',
            'hover-hover:hover:bg-dark-ui-100': backgroundLuminance === 'dark',
          },
          {
            'hover-hover:hover:text-dark-text-100': backgroundLuminance === 'light',
            'hover-hover:hover:text-light-text-100': backgroundLuminance === 'dark',
          },

          // Active
          {
            'hover-hover:active:bg-grape-500': backgroundLuminance === 'light',
            'hover-hover:active:bg-canary-500': backgroundLuminance === 'dark',
          },
          {
            'hover-hover:active:text-light-text-100': backgroundLuminance === 'light',
            'hover-hover:active:text-dark-text-100': backgroundLuminance === 'dark',
          },
        ],

        sectionAddPostButton.disabled && [
          // Base
          {
            'bg-[rgba(255,255,255,0.15)]': backgroundLuminance === 'light',
            'bg-[rgba(0,0,0,0.1)]': backgroundLuminance === 'dark',
            'text-dark-text-300': backgroundLuminance === 'light',
            'text-light-text-300': backgroundLuminance === 'dark',
          },
        ],
      ]"
      :data-testid="sectionAddPostButton.testId"
      @click="sectionAddPostButton.onClick"
      @focus="
        sectionAddPostButton.disabled && sectionAddPostButton.disabledTooltipText
          ? (xAddPostDisabledButtonTooltip = true)
          : null
      "
      @blur="xAddPostDisabledButtonTooltip = false"
      @mouseenter="
        sectionAddPostButton.disabled && sectionAddPostButton.disabledTooltipText
          ? (xAddPostDisabledButtonTooltip = true)
          : null
      "
      @mouseleave="xAddPostDisabledButtonTooltip = false"
      @touchstart="
        sectionAddPostButton.disabled && sectionAddPostButton.disabledTooltipText
          ? (xAddPostDisabledButtonTooltip = !xAddPostDisabledButtonTooltip)
          : null
      "
    >
      <OzIcon :name="sectionAddPostButton.icon" :size="16" />
    </OzBaseButton>
    <OzTooltip
      v-if="xAddPostDisabledButtonTooltip && addPostButtonRef"
      :anchor="addPostButtonRef.$el"
      :config="{
        placement: 'top',
        middleware: [
          offset({
            mainAxis: 8,
          }),
          flip(),
          shift(),
        ],
      }"
      class="max-w-xs z-10"
    >
      <p>{{ sectionAddPostButton.disabledTooltipText }}</p>
    </OzTooltip>
  </header>
</template>

<style scoped>
@media (hover: hover) {
  .hover-overlay-light:hover {
    box-shadow: inset 0 0 0 1000px rgba(0, 0, 0, 0.05);
  }

  .hover-overlay-dark:hover {
    box-shadow: inset 0 0 0 1000px rgba(255, 255, 255, 0.1);
  }
}
</style>
